import "./MapPreview.css";

import { Controller } from "@hotwired/stimulus";
import maplibregl from "maplibre-gl";

type PreviewRouteCoordinate = {
    latitude?: number | null;
    longitude?: number | null;
};

type PreviewRoutePoiMarker = {
    id?: string | null;
    order?: number | null;
    latitude?: number | null;
    longitude?: number | null;
};

const routeSourceId = "smartguide-map-preview-route";
const routeLayerId = "smartguide-map-preview-route";
const routePoiMarkerSourceId = "smartguide-map-preview-route-poi-markers";
const routePoiMarkerLayerId = "smartguide-map-preview-route-poi-markers";
const routePoiMarkerImageId = "smartguide-map-preview-route-poi-marker";
const routeLineWidth = 2.5;

export default class MapPreviewController extends Controller<HTMLElement> {
    static values = {
        latitude: Number,
        longitude: Number,
        title: String,
        styleUrl: String,
        routeCoordinates: String,
        routePoiMarkers: String
    };

    declare readonly latitudeValue: number;
    declare readonly longitudeValue: number;
    declare readonly titleValue: string;
    declare readonly styleUrlValue: string;
    declare readonly routeCoordinatesValue: string;
    declare readonly routePoiMarkersValue: string;
    private map?: maplibregl.Map;

    connect(): void {
        document.addEventListener("turbo:before-cache", this.onBeforeCache);

        const routeCoordinates = this.routeCoordinates;
        const routePoiMarkers = this.previewRoutePoiMarkers;
        const routeBounds = this.routeBounds(routeCoordinates);
        const center: [number, number] = routeCoordinates.length > 0
            ? [routeCoordinates[0].longitude!, routeCoordinates[0].latitude!]
            : [this.longitude, this.latitude];

        if (!this.webGlIsAvailable()) {
            this.showFallbackMarker();
            return;
        }

        try {
            this.map = new maplibregl.Map({
                container: this.element,
                style: this.styleUrl,
                center,
                zoom: 16,
                interactive: false,
                attributionControl: false,
                logoPosition: "bottom-left"
            });

            if (routeBounds) {
                this.map.fitBounds(routeBounds, {
                    padding: { top: 25, right: 60, bottom: 25, left: 60 },
                    duration: 0
                });
                this.whenStyleReady(() => {
                    this.renderRoute(routeCoordinates);
                    this.renderRoutePoiMarkers(routePoiMarkers);
                });
            } else {
                this.map.fitBounds(this.boundsAround(center, 100), {
                    padding: { top: 70, right: 60, bottom: 25, left: 60 },
                    duration: 0
                });

                const marker = document.createElement("div");
                marker.className = "smartguide-map-preview__marker smartguide-map-preview__marker--selected";
                marker.setAttribute("aria-label", this.title);
                new maplibregl.Marker({ element: marker }).setLngLat(center).addTo(this.map);
            }
            this.clearFallbackMarker();
        } catch {
            this.teardownMap();
            this.showFallbackMarker();
        }
    }

    disconnect(): void {
        document.removeEventListener("turbo:before-cache", this.onBeforeCache);
        this.teardownMap();
    }

    private readonly onBeforeCache = (): void => {
        this.teardownMap();
    };

    private teardownMap(): void {
        this.map?.remove();
        this.map = undefined;
        this.clearFallbackMarker();
    }

    private boundsAround(center: [number, number], meters: number): [[number, number], [number, number]] {
        const [longitude, latitude] = center;
        const latitudeDelta = meters / 111320;
        const longitudeDelta = meters / (111320 * Math.cos(latitude * Math.PI / 180));
        return [
            [longitude - longitudeDelta, latitude - latitudeDelta],
            [longitude + longitudeDelta, latitude + latitudeDelta]
        ];
    }

    private get latitude(): number {
        return this.latitudeValue || Number(this.element.dataset.smartguideMapPreviewLatitudeValue);
    }

    private get longitude(): number {
        return this.longitudeValue || Number(this.element.dataset.smartguideMapPreviewLongitudeValue);
    }

    private get title(): string {
        return this.titleValue || this.element.dataset.smartguideMapPreviewTitleValue || "";
    }

    private get styleUrl(): string {
        return this.styleUrlValue || this.element.dataset.smartguideMapPreviewStyleUrlValue || "";
    }

    private get routeCoordinates(): PreviewRouteCoordinate[] {
        const rawValue = this.routeCoordinatesValue || this.element.dataset.smartguideMapPreviewRouteCoordinatesValue || "";
        if (!rawValue.trim()) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawValue) as PreviewRouteCoordinate[];
            return Array.isArray(parsed)
                ? parsed.filter(coordinate => Number.isFinite(coordinate?.latitude) && Number.isFinite(coordinate?.longitude))
                : [];
        } catch {
            return [];
        }
    }

    private get previewRoutePoiMarkers(): PreviewRoutePoiMarker[] {
        const rawValue = this.routePoiMarkersValue || this.element.dataset.smartguideMapPreviewRoutePoiMarkersValue || "";
        if (!rawValue.trim()) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawValue) as PreviewRoutePoiMarker[];
            return Array.isArray(parsed)
                ? parsed.filter(marker =>
                    Boolean(marker?.id) &&
                    Number.isFinite(marker?.order) &&
                    Number.isFinite(marker?.latitude) &&
                    Number.isFinite(marker?.longitude))
                : [];
        } catch {
            return [];
        }
    }

    private routeBounds(routeCoordinates: PreviewRouteCoordinate[]): [[number, number], [number, number]] | undefined {
        if (routeCoordinates.length < 2) {
            return undefined;
        }

        const latitudes = routeCoordinates.map(coordinate => coordinate.latitude!);
        const longitudes = routeCoordinates.map(coordinate => coordinate.longitude!);

        return [
            [Math.min(...longitudes), Math.min(...latitudes)],
            [Math.max(...longitudes), Math.max(...latitudes)]
        ];
    }

    private whenStyleReady(callback: () => void): void {
        if (!this.map) {
            return;
        }

        if (this.map.isStyleLoaded?.() !== false) {
            callback();
            return;
        }

        this.map.once("load", callback);
    }

    private renderRoute(routeCoordinates: PreviewRouteCoordinate[]): void {
        if (!this.map || routeCoordinates.length < 2) {
            return;
        }

        const featureCollection: any = {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: routeCoordinates.map(coordinate => [coordinate.longitude, coordinate.latitude])
                    },
                    properties: {}
                }
            ]
        };
        const existingSource = this.map.getSource(routeSourceId) as maplibregl.GeoJSONSource | undefined;
        if (existingSource?.setData) {
            existingSource.setData(featureCollection);
        } else {
            this.map.addSource(routeSourceId, {
                type: "geojson",
                data: featureCollection
            });
        }

        if (!this.map.getLayer(routeLayerId)) {
            this.map.addLayer({
                id: routeLayerId,
                type: "line",
                source: routeSourceId,
                layout: {
                    "line-cap": "round",
                    "line-join": "round"
                },
                paint: {
                    "line-color": "rgb(6, 97, 224)",
                    "line-width": routeLineWidth,
                    "line-opacity": 1
                }
            });
        }
    }

    private renderRoutePoiMarkers(markers: PreviewRoutePoiMarker[]): void {
        if (!this.map || markers.length === 0) {
            return;
        }

        this.ensureRoutePoiMarkerImage();
        const featureCollection: any = {
            type: "FeatureCollection",
            features: markers.map(marker => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [marker.longitude, marker.latitude]
                },
                properties: {
                    id: marker.id,
                    order: marker.order,
                    sortKey: marker.order
                }
            }))
        };
        const existingSource = this.map.getSource(routePoiMarkerSourceId) as maplibregl.GeoJSONSource | undefined;
        if (existingSource?.setData) {
            existingSource.setData(featureCollection);
        } else {
            this.map.addSource(routePoiMarkerSourceId, {
                type: "geojson",
                data: featureCollection
            });
        }

        if (!this.map.getLayer(routePoiMarkerLayerId)) {
            this.map.addLayer({
                id: routePoiMarkerLayerId,
                type: "symbol",
                source: routePoiMarkerSourceId,
                layout: {
                    "icon-image": routePoiMarkerImageId,
                    "icon-size": 0.5,
                    "icon-allow-overlap": false,
                    "icon-ignore-placement": false,
                    "text-field": ["to-string", ["get", "order"]],
                    "text-size": 14,
                    "text-font": ["Noto Sans Bold"],
                    "text-allow-overlap": false,
                    "text-ignore-placement": false,
                    "symbol-sort-key": ["get", "sortKey"]
                },
                paint: {
                    "text-color": "rgb(255, 255, 255)"
                }
            });
        }
    }

    private ensureRoutePoiMarkerImage(): void {
        if (!this.map || this.map.hasImage?.(routePoiMarkerImageId)) {
            return;
        }

        const imageData = this.createRoutePoiMarkerImage();
        if (!imageData) {
            return;
        }

        this.map.addImage(routePoiMarkerImageId, imageData);
    }

    private createRoutePoiMarkerImage(): ImageData | undefined {
        if (typeof ImageData === "undefined") {
            return undefined;
        }

        const size = 56;
        const radius = size / 2;
        const center = radius - 0.5;
        const data = new Uint8ClampedArray(size * size * 4);

        for (let y = 0; y < size; y += 1) {
            for (let x = 0; x < size; x += 1) {
                const dx = x - center;
                const dy = y - center;
                if ((dx * dx) + (dy * dy) > radius * radius) {
                    continue;
                }

                const offset = ((y * size) + x) * 4;
                data[offset] = 6;
                data[offset + 1] = 97;
                data[offset + 2] = 224;
                data[offset + 3] = 255;
            }
        }

        return new ImageData(data, size, size);
    }

    private webGlIsAvailable(): boolean {
        try {
            const canvas = document.createElement("canvas");
            return Boolean(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
        } catch {
            return false;
        }
    }

    private showFallbackMarker(): void {
        this.element.classList.add("smartguide-map-preview--unavailable");
        this.element.dataset.smartguideMapPreviewUnavailable = "true";

        if (this.element.querySelector(".smartguide-map-preview__fallback-marker")) {
            return;
        }

        const marker = document.createElement("div");
        marker.className = "smartguide-map-preview__marker smartguide-map-preview__marker--selected smartguide-map-preview__fallback-marker";
        marker.setAttribute("aria-label", this.title);
        this.element.append(marker);
    }

    private clearFallbackMarker(): void {
        this.element.classList.remove("smartguide-map-preview--unavailable");
        delete this.element.dataset.smartguideMapPreviewUnavailable;
        this.element.querySelectorAll(".smartguide-map-preview__fallback-marker").forEach(marker => marker.remove());
    }
}
